import { Component, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { AsmService } from '../../services/asm/asm.service';
import { TranslocoModule } from '@ngneat/transloco';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, takeUntil } from 'rxjs';
import { UserService } from '../../core/user/user.service';
import { User } from '../../core/user/user.types';
import { Vulnerability, VulnerabilitySeverity, VulnerabilitySeverityMap } from '../../interfaces/asm/models';
import { QueryVulnerabilitiesResponse } from '../../interfaces/asm/QueryVulnerabilitiesResponse';

@Component({
    selector: 'app-a-vulnerabilities',
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        MatTooltipModule,
        TranslocoModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './a-vulnerabilities.component.html',
    styleUrl: './a-vulnerabilities.component.scss',
})
export class AVulnerabilitiesComponent {
    displayedColumns: string[] = [
        'source',
        'title',
        'severity',
        'vulnerableUrl',
        'status',
        'action',
    ];
    data: Vulnerability[] = [];
    dataSource: MatTableDataSource<Vulnerability>;
    totalItems = 0;
    pageSizes = [10, 20, 50, 1000];
    isLoadingResults = true;
    isRateLimitReached = false;

    user: User;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    // isToggled
    isToggled = false;

    constructor(
        public themeService: CustomizerSettingsService,
        private _userService: UserService,
        private asmService: AsmService
    ) {
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to the user service
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;

                this.asmService
                    .getQueryVulnerabilities(this.user.slug)
                    .subscribe((response: QueryVulnerabilitiesResponse) => {
                        this.isLoadingResults = false;
                        this.data = response.vulnerabilities.sort((a, b) => {
                            return this.severityCode(b.severity.toString()) - this.severityCode(a.severity.toString());
                        });
                        this.totalItems = this.data.length;

                        // Assign the data to the data source for the table to render
                        this.dataSource = new MatTableDataSource(this.data);
                        this.dataSource.paginator = this.paginator;
                        this.dataSource.sort = this.sort;
                    });
            });
    }

    /**
     * After View Init
     */
    ngAfterViewInit() {}

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    // Search Filter
    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    // onKeydown
    onKeydown(event: Event) {
        //console.log(event);
        return false;
    }

    // severityCode
    severityCode(severity: String): number {
        switch (severity) {
            case VulnerabilitySeverityMap[VulnerabilitySeverity.INFO]:
                return VulnerabilitySeverity.INFO;
            case VulnerabilitySeverityMap[VulnerabilitySeverity.LOW]:
                return VulnerabilitySeverity.LOW;
            case VulnerabilitySeverityMap[VulnerabilitySeverity.MEDIUM]:
                return VulnerabilitySeverity.MEDIUM;
            case VulnerabilitySeverityMap[VulnerabilitySeverity.HIGH]:
                return VulnerabilitySeverity.HIGH;
            case VulnerabilitySeverityMap[VulnerabilitySeverity.CRITICAL]:
                return VulnerabilitySeverity.CRITICAL;
            default:
                return VulnerabilitySeverity.UNKNOWN;
        }
    }
}
